import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { buildSidebarSections } from '@/layouts/sidebar/sidebarNavBuilders';
import { preloadRouteForPath } from '@/navigation/routes/routePreload';
import { useNotifications } from '@/providers/notificationHooks';
import { getDevPlanOverrideSummary } from '@/shared/plans/devPlanOverrides';
import { layoutSizes } from '@/theme/layoutSizes';
import { fontFamilies, s, typographyTokens } from '@/theme/tokens';
import { r } from '@/theme/tokens/radii';
import { Box, Chip, Inline, Text } from '@/ui';
import { StatusBanner } from '@/ui/composites';
import { SidebarNav } from '@/ui/composites/SidebarNav';
import { AppShell } from '@/ui/layout/AppShell';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { Button, PageShell } from '@/ui/primitives';
import type { AuthSession } from '@/auth/types';
import type { NavItem } from '@/components/sidebarTypes';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';
import type { PlanAlert, PlanBadge } from '@/shared/plans/planStatus';
import type {
  OrganisationDto,
  OrganisationEntitlementsDto,
  PlanCode,
  ProjectDto,
} from '@flowqa/shared';
import type { ReactElement, ReactNode } from 'react';

const TopBarActions = lazy(async () => {
  const module = await import('@/components/TopBarActions');
  return { default: module.TopBarActions };
});

type AuthenticatedLayoutProps = {
  session: AuthSession | null;
  isAdmin: boolean;
  isSuperuser: boolean;
  navItems: NavItem[];
  sidebarFeatureFlags?: FeatureFlagResolveResult['flags'] | null;
  addOns?: OrganisationEntitlementsDto['addOns'] | null;
  hideSidebar?: boolean;
  showSidebarWhenSignedOut?: boolean;
  forceShowSignOut?: boolean;
  restrictTopBarToSignOut?: boolean;
  disableTopBarSignOut?: boolean;
  activePath: string;
  projects: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'> & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  allProjects: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'> & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  canCreateProject?: boolean;
  planName?: string | null;
  planCode?: PlanCode | null;
  planAlerts?: PlanAlert[];
  planBadge?: PlanBadge | null;
  uploadBlockedReason?: string | null;
  lockProjectSelector?: boolean;
  onNavigate: (path: string) => void;
  onAddProject: () => void;
  onAddOrganisation: () => void;
  onOpenFeedback: () => void;
  onSignOut: () => void;
  children?: ReactNode;
};

export const AuthenticatedLayout = ({
  session,
  isAdmin,
  isSuperuser,
  navItems,
  sidebarFeatureFlags,
  addOns = null,
  hideSidebar = false,
  showSidebarWhenSignedOut = false,
  forceShowSignOut = false,
  restrictTopBarToSignOut = false,
  disableTopBarSignOut = false,
  activePath,
  projects,
  allProjects,
  organisations,
  canCreateProject = true,
  planName = null,
  planCode = null,
  planAlerts = [],
  planBadge = null,
  uploadBlockedReason = null,
  lockProjectSelector = false,
  onNavigate,
  onAddProject,
  onAddOrganisation,
  onOpenFeedback,
  onSignOut,
  children,
}: AuthenticatedLayoutProps): ReactElement => {
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const [searchParams] = useSearchParams();
  const { notify } = useNotifications();
  const layoutMode = useLayoutMode();
  const useFixedTopbarOrgWidth = layoutMode === layoutModeTokens.wide;
  const resolvedProjectOptions = useMemo(
    () => (projects.length > 0 ? projects : allProjects),
    [allProjects, projects],
  );
  const projectLabel = useMemo(() => {
    if (selectedProjectId) {
      const selectedProject = resolvedProjectOptions.find(
        (project) => project.id === selectedProjectId,
      );
      return selectedProject?.name ?? 'All Projects';
    }
    if (lockProjectSelector && resolvedProjectOptions.length === 1) {
      return resolvedProjectOptions[0]?.name ?? 'All Projects';
    }
    return 'All Projects';
  }, [lockProjectSelector, resolvedProjectOptions, selectedProjectId]);
  const selectedSidebarProject = useMemo(() => {
    if (selectedProjectId) {
      return resolvedProjectOptions.find((project) => project.id === selectedProjectId) ?? null;
    }
    if (lockProjectSelector && resolvedProjectOptions.length === 1) {
      return resolvedProjectOptions[0] ?? null;
    }
    return null;
  }, [lockProjectSelector, resolvedProjectOptions, selectedProjectId]);
  const selectedOrganisationName =
    selectedOrganisationId === null
      ? (session?.orgName ?? null)
      : (organisations.find((org) => org.id === selectedOrganisationId)?.name ??
        (session?.orgId === selectedOrganisationId ? (session.orgName ?? null) : null));
  const showTopbarOrganisationName = activePath !== '/onboarding';
  const topbarOrganisationLabel = selectedOrganisationName ?? 'Workspace';
  const showTopbarOrganisationSeparator = Boolean(selectedOrganisationName);
  const selectedSidebarProjectLocked = selectedSidebarProject?.locked === true;
  const planAlertStorageKey = useMemo(() => {
    const resolvedOrganisationKey = selectedOrganisationId ?? session?.orgId ?? 'global';
    const resolvedUserKey = session?.userId ?? 'anonymous';
    return `flowqa-plan-alerts:${resolvedUserKey}:${resolvedOrganisationKey}`;
  }, [selectedOrganisationId, session?.orgId, session?.userId]);
  const [dismissedPlanAlerts, setDismissedPlanAlerts] = useState<string[]>([]);
  const devPlanOverrideSummary = useMemo(
    () => getDevPlanOverrideSummary(searchParams),
    [searchParams],
  );

  const shouldRenderSidebar = !hideSidebar && (Boolean(session) || showSidebarWhenSignedOut);
  const sidebarContent = useMemo(
    () =>
      buildSidebarSections({
        session,
        navItems,
        activePath,
        onNavigate,
        preloadRoute: preloadRouteForPath,
        featureFlags: sidebarFeatureFlags,
        addOns,
        isAdmin,
        isSuperuser,
        planName,
        planCode,
        selectedOrganisationId,
        selectedProjectId,
        setSelectedOrganisationId,
        setSelectedProjectId,
        notify,
        projectsOptions: resolvedProjectOptions,
        allProjectsOptions: allProjects,
        organisationsOptions: organisations,
        canCreateProject,
        lockProjectSelector,
        projectLabel,
        projectBadge:
          selectedSidebarProjectLocked || !selectedSidebarProject?.badgeEnabled
            ? null
            : {
                chars: selectedSidebarProject.badgeChars,
                color: selectedSidebarProject.badgeColor,
              },
        projectLocked: selectedSidebarProjectLocked,
        planBadge,
        onAddProject,
        onAddOrganisation,
        onOpenFeedback,
      }),
    [
      activePath,
      canCreateProject,
      isAdmin,
      isSuperuser,
      navItems,
      notify,
      onAddOrganisation,
      onOpenFeedback,
      onAddProject,
      onNavigate,
      allProjects,
      addOns,
      organisations,
      planName,
      planCode,
      resolvedProjectOptions,
      selectedOrganisationId,
      selectedProjectId,
      setSelectedOrganisationId,
      setSelectedProjectId,
      lockProjectSelector,
      projectLabel,
      planBadge,
      selectedSidebarProject,
      selectedSidebarProjectLocked,
      session,
      sidebarFeatureFlags,
    ],
  );

  const sidebar = shouldRenderSidebar ? (
    <SidebarNav
      sections={sidebarContent.sections}
      preFooterItems={sidebarContent.preFooterItems}
      footerItems={sidebarContent.footerItems}
    />
  ) : null;
  const restrictTopBarNavigation = activePath === '/onboarding' || restrictTopBarToSignOut;
  const shouldShowFallbackSignOut = forceShowSignOut || !session || restrictTopBarToSignOut;
  const [showDeferredTopBarActions, setShowDeferredTopBarActions] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const raw = window.localStorage.getItem(planAlertStorageKey);
      if (!raw) {
        setDismissedPlanAlerts([]);
        return;
      }
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        setDismissedPlanAlerts([]);
        return;
      }
      setDismissedPlanAlerts(parsed.filter((item): item is string => typeof item === 'string'));
    } catch {
      setDismissedPlanAlerts([]);
    }
  }, [planAlertStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(planAlertStorageKey, JSON.stringify(dismissedPlanAlerts));
  }, [dismissedPlanAlerts, planAlertStorageKey]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }
    if (searchParams.get('devResetPlanAlerts') !== '1') {
      return;
    }
    try {
      window.localStorage.removeItem(planAlertStorageKey);
      setDismissedPlanAlerts([]);
    } catch {
      // ignore storage failures
    }
  }, [planAlertStorageKey, searchParams]);

  useEffect(() => {
    if (restrictTopBarNavigation || shouldShowFallbackSignOut) {
      setShowDeferredTopBarActions(false);
      return;
    }
    if (typeof window === 'undefined') {
      setShowDeferredTopBarActions(true);
      return;
    }
    let rafId: number | null = null;
    let timeoutId: number | null = null;
    const scheduleLoad = (): void => {
      timeoutId = window.setTimeout(() => {
        setShowDeferredTopBarActions(true);
      }, 200);
    };
    if (typeof window.requestAnimationFrame === 'function') {
      rafId = window.requestAnimationFrame(scheduleLoad);
    } else {
      scheduleLoad();
    }
    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [restrictTopBarNavigation, shouldShowFallbackSignOut]);
  const visiblePlanAlerts = useMemo(
    () => planAlerts.filter((alert) => !dismissedPlanAlerts.includes(alert.key)),
    [dismissedPlanAlerts, planAlerts],
  );
  const hasTopStatusBanners =
    devPlanOverrideSummary.length > 0 ||
    visiblePlanAlerts.length > 0 ||
    (uploadBlockedReason !== null && selectedSidebarProjectLocked);
  const topBarActionsFallback = (
    <Inline gap="sm" align="center" sx={{ width: 'auto', pr: s('sm') }}>
      <Box
        sx={{
          width: `calc(${s('2xl')} + ${s('md')})`,
          height: `calc(${s('2xl')} + ${s('md')})`,
          borderRadius: r('pill'),
        }}
      />
    </Inline>
  );
  const actions =
    restrictTopBarNavigation || shouldShowFallbackSignOut ? (
      <Inline gap="md" align="center" sx={{ width: 'auto' }}>
        {disableTopBarSignOut ? null : (
          <Button variant="text" onClick={onSignOut}>
            Sign Out
          </Button>
        )}
      </Inline>
    ) : showDeferredTopBarActions ? (
      <Suspense fallback={topBarActionsFallback}>
        <TopBarActions session={session} actionsGap="sm" />
      </Suspense>
    ) : (
      topBarActionsFallback
    );

  const titleCore = (
    <>
      <Box
        component="img"
        src="/logo.png"
        alt="FlowQA"
        sx={{ height: '36px', width: '36px', display: 'block' }}
      />
      <Text
        role="label"
        component="span"
        sx={{
          fontFamily: fontFamilies.mono,
          fontSize: 24,
          fontWeight: typographyTokens.bodyStrong.fontWeight,
          lineHeight: 1,
        }}
      >
        [FLOWQA]
      </Text>
      {session ? (
        <Chip label="Beta" size="xs" tone="pink" transform="uppercase" sx={{ ml: s('xs') }} />
      ) : null}
    </>
  );
  const titleContent = (
    <Box
      sx={{
        color: 'inherit',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: s('sm'),
        paddingTop: s('md'),
        paddingBottom: s('none'),
      }}
    >
      {restrictTopBarNavigation ? (
        titleCore
      ) : (
        <Box
          component={RouterLink}
          to="/execution-runs"
          sx={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: s('sm') }}>{titleCore}</Box>
        </Box>
      )}
      {showTopbarOrganisationName ? (
        <Box
          component="span"
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            alignItems: 'center',
            gap: s('md'),
            marginLeft: s('sm'),
            minWidth: useFixedTopbarOrgWidth ? `calc(${s('5xl')} + ${s('3xl')})` : 'auto',
          }}
        >
          {showTopbarOrganisationSeparator ? (
            <Text
              role="body"
              component="span"
              sx={{
                fontSize: 19,
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                marginBottom: s('md'),
              }}
            >
              ·
            </Text>
          ) : null}
          <Text
            role="body"
            component="span"
            sx={{
              fontSize: 19,
              fontWeight: typographyTokens.label.fontWeight,
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              marginBottom: s('md'),
              whiteSpace: 'nowrap',
              maxWidth: useFixedTopbarOrgWidth ? `calc(${s('5xl')} + ${s('3xl')})` : 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              visibility: selectedOrganisationName ? 'visible' : 'hidden',
            }}
          >
            {topbarOrganisationLabel}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
  const content = (
    <Box
      key={activePath}
      sx={{ width: '100%', maxWidth: layoutSizes.content.maxWidth, mx: 'auto', minWidth: 0 }}
    >
      {hasTopStatusBanners ? (
        <Box sx={{ pt: s('xl') }}>
          <PageShell
            maxWidth="xl"
            paddingX="lg"
            paddingY="none"
            paddingTop="none"
            paddingBottom="none"
          >
            {devPlanOverrideSummary.length > 0 ? (
              <Box sx={{ mb: s('none') }}>
                <StatusBanner
                  tone="blue"
                  message={`Dev plan preview active: ${devPlanOverrideSummary.join(' · ')}`}
                  dense
                />
              </Box>
            ) : null}
            {visiblePlanAlerts.length > 0 ? (
              <Box sx={{ mb: s('none') }}>
                {visiblePlanAlerts.map((alert, index) => (
                  <Box key={alert.key} sx={index === 0 ? undefined : { mt: s('sm') }}>
                    <StatusBanner
                      tone={alert.tone}
                      message={alert.message}
                      onClose={() => {
                        setDismissedPlanAlerts((current) =>
                          current.includes(alert.key) ? current : [...current, alert.key],
                        );
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : null}
            {uploadBlockedReason && selectedSidebarProjectLocked ? (
              <Box sx={{ mb: s('none') }}>
                <StatusBanner tone="orange" message={uploadBlockedReason} />
              </Box>
            ) : null}
          </PageShell>
        </Box>
      ) : null}
      {children}
    </Box>
  );

  return (
    <AppShell title={titleContent} actions={actions} sidebar={sidebar} showDefaultActions={false}>
      {content}
    </AppShell>
  );
};
